import {Fragment, memo} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {headingXS} from '~/shared/theme/typography';
import {FlexColumn} from '~/shared/theme/FlexLayout';
import {media} from '~/shared/theme/media';
import PieButton from '~/shared/theme/pie/PieButton';
import PiePlusIcon from '~/assets/images/icons/pie/functionalityPlusSmall.svg?react';
import {flipOnLTR} from '~/shared/theme/utils';
import {capitalFirstLetterAfterSpace} from '~/shared/utils/general';
import {
  isUserCreditCard,
  isUserPaypal,
  UserCreditCard,
  UserPaypal,
} from '~/personalArea/components/Sections/Payments/types';
import {getLegacySiteUrl} from '~/shared/services/urlService';
import TenbisIcon from '~/shared/utils/payments/cardsPie/moneycardSmall.svg?react';

import Payment from './Payment';

const Root = styled(FlexColumn)`
  justify-content: space-between;
  ${media.minLargeMobile`
    padding: 0 32px;
  `}
`;

const Separator = styled.div`
  background: rgba(0, 0, 0, 0.08);
  height: 1px;
`;

const Title = styled.p`
  ${headingXS}
  color: ${({theme}) => theme.colors.contentDefault};
  padding: 0 16px;
  margin-bottom: 8px;
  ${media.minLargeMobile`
    padding: 0;
    margin-bottom: 8px;
  `}
`;

const AddPaymentButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 16px;
  ${media.minLargeMobile`
    padding: 0;
    gap: 8px;
  `}
`;

const AddPaymentButton = styled(PieButton).attrs({variant: 'primaryLarge'})`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 10px 24px;
  margin-top: 16px;
  ${media.minLargeMobile`
    width: 50%;
  `}
  ${media.minLargeTablet`
    width: auto;
  `}
`;

const StyledPiePlusIcon = styled(PiePlusIcon)`
  margin-bottom: 2px;
  ${flipOnLTR`
    margin-left: 6px;
  `}
`;

const ManageTenbisCardButton = styled(PieButton).attrs({
  variant: 'outlineLarge',
  forwardedAs: 'a' as const,
  href: getLegacySiteUrl('Account/UserAccountManagement'),
})`
  margin-top: 16px;
  width: 50%;

  svg {
    ${flipOnLTR`
      margin-left: 8px;
    `}
  }

  ${media.minLargeTablet`
    width: auto;
  `}
`;

type PaymentsListProps = {
  payments: (UserCreditCard | UserPaypal)[];
  onRemovePaymentClick: (args: {id: number; cardName: string; type: string}) => void;
  onAddPaymentMethodClick: () => void;
  isB2B: boolean;
  isMinLargeMobile?: boolean;
};

const PaymentsList = ({
  payments,
  onRemovePaymentClick,
  onAddPaymentMethodClick,
  isB2B,
  isMinLargeMobile,
}: PaymentsListProps) => {
  const {t} = getLocalizationService();

  const addPaymentClickHandler = () => {
    onAddPaymentMethodClick();
  };

  const addPaymentButtonProps = isB2B
    ? {
        forwardedAs: 'a' as const,
        href: getLegacySiteUrl('/Moneycard/MoneycardOtlRuleManagement'),
      }
    : {
        onClick: addPaymentClickHandler,
      };

  return (
    <Root>
      <div>
        <Title data-test-id="paymentMethodsSectionTitle">{t('payment_methods')}</Title>
        {payments.map((payment, index) => (
          <Fragment key={`personal_area_payment_${payment.id}`}>
            <Payment
              dataTest={`paymentIndex_${index}`}
              onRemovePaymentClick={onRemovePaymentClick}
              isB2B={isB2B}
              id={payment.id}
              type={payment.type}
              {...(isUserCreditCard(payment) ? {
                brand: payment.brand,
                suffix: payment.suffix,
                expiryDate: payment.expiration,
                isExpired: payment.isExpired,
              } : isUserPaypal(payment) ? {
                email: payment.email,
              } : {})}
            />
            {(index !== payments.length - 1 || isMinLargeMobile) && <Separator />}
          </Fragment>
      ))}
      </div>
      <AddPaymentButtonContainer>
        {isMinLargeMobile && isB2B && (
          <ManageTenbisCardButton>
            <TenbisIcon />
            {t('tenbis_card_management')}
          </ManageTenbisCardButton>
        )}
        <AddPaymentButton
          data-test-id="addPaymentBtn"
          {...addPaymentButtonProps}
        >
          <StyledPiePlusIcon />
          {capitalFirstLetterAfterSpace(t('add_payment_method'))}
        </AddPaymentButton>
      </AddPaymentButtonContainer>
    </Root>
  );
};

export default memo(PaymentsList);
